import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Skeleton } from "~/components/ui/skeleton";
import { Card } from "~/components/ui/card";
import BlogCard from "../Card/BlogCard";
import MobileLayout from "./MobileLayout";
import DesktopLayout from "./DesktopLayout";
import BlogsService from "~/lib/services/BlogsService";

interface BlogsCategoryProps {
    blog: any;
    title: string;
    params: any;
    region: string;
    featuredBlogs: any;
    isMobile: boolean;
    country: string;
    category: string;
    blogCategory?: string;
    blogSubCategory?: string;
    showFilterChips?: boolean;
    selectedChip?: string;
    /** Uses Co-Stats–style hero instead of featured blog hero (news, market intel, case study, community) */
    marketingHero?: boolean;
    /** Key for hero copy in blogChipMarketingHero; falls back to selectedChip when omitted */
    marketingHeroCopyKey?: string;
}

export default function BlogsCategory({
    blog,
    title,
    params,
    region,
    featuredBlogs,
    isMobile,
    country,
    category,
    blogCategory,
    blogSubCategory,
    showFilterChips = false,
    selectedChip,
    marketingHero = false,
    marketingHeroCopyKey,
}: BlogsCategoryProps) {


    const ITEMS_PER_PAGE = isMobile ? 5 : 12;

    params = { ...params, country: region };
    const totalPages = Math.ceil(blog?.pageInfo?.totalBlogs / ITEMS_PER_PAGE);
    const [currentPage, setCurrentPage] = useState(1);
    const [blogsData, setBlogsData] = useState<any>();
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<string[]>(selectedChip ? [selectedChip] : ['All']);
    const [searchText, setSearchText] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);



    const getBlogs = useCallback(async (categories: string[], search: string, page = 1) => {
        setLoading(true);
        const updatedCategories = categories.filter((t: string) => t !== 'All');
        const categoryFilter = selectedChip
            ? selectedChip
            : updatedCategories.join();
        const resolvedBlogCategory = blogCategory || categoryFilter;
        const resolvedBlogSubCategory = blogSubCategory || "";
        const blogsFetchData = await BlogsService.fetchBlogsByCategories(
            page,
            ITEMS_PER_PAGE,
            country,
            resolvedBlogCategory,
            resolvedBlogSubCategory,
            search,
            category,
        );
        setBlogsData(blogsFetchData);
        setLoading(false);
    }, [ITEMS_PER_PAGE, country, category, selectedChip, blogCategory, blogSubCategory]);

    useEffect(() => {
        setBlogsData(blog);
        setCurrentPage(1);
        setSearchText("");
        setHasSearched(false);
        setSelected(selectedChip ? [selectedChip] : ["All"]);
    }, [blog, region, selectedChip]);

    const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setCurrentPage(1);
            getBlogs([], value);
            setHasSearched(true);
        }, 1000);
    };


    const renderBlogs = () => {
        if (loading) {
            return Array.from({ length: ITEMS_PER_PAGE }, (_, idx: number) => (
                <Card key={idx} className="min-w-[250px] w-full rounded-xl shadow-sm">
                    <Skeleton className="min-w-[250px] h-48 w-full" />
                </Card>
            ));
        }
        return blogsData?.blogs?.map((article: any, idx: number) => (
            <Suspense fallback={<Skeleton className="h-48 w-full" />} key={article.id || idx}>
                <BlogCard country={country} article={article} />
            </Suspense>
        ));
    };
    const handlePageChange = useCallback((newPage: number) => {
        if (newPage < 1 || newPage > totalPages) return;
        setCurrentPage(newPage);
        getBlogs([], searchText, newPage);
    }, [totalPages, searchText, getBlogs]);
    const clearFilters = useCallback(() => {
        setSelected(selectedChip ? [selectedChip] : ['All']);
        setCurrentPage(1);
        setSearchText('');
        getBlogs([], "", 1);
    }, [getBlogs, selectedChip]);

    const toggleTopic = useCallback((topic: string) => {
        setCurrentPage(1);
        if (topic === 'All') {
            if (!selected.includes('All')) {
                setSelected(['All']);
                getBlogs([], searchText);
            }
        } else {
            let categories = selected.includes(topic)
                ? selected.filter(t => t !== topic && t !== 'All')
                : [...selected.filter(t => t !== 'All'), topic];
            setSelected(categories);
            getBlogs(categories, searchText);
        }
    }, [selected, getBlogs, searchText]);

    return (
        isMobile ?
            <MobileLayout
                featuredBlogs={featuredBlogs}
                renderBlogs={renderBlogs}
                onSearch={onSearch}
                searchText={searchText}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                pageInfo={blogsData?.pageInfo}
                title={title}
                noResults={blogsData?.blogs?.length < 1}
                clearFilters={clearFilters}
                country={country}
                marketingHero={marketingHero}
                marketingHeroCopyKey={marketingHeroCopyKey}
                selectedChip={selectedChip}
            />
            : <DesktopLayout
                featuredBlogs={featuredBlogs}
                renderBlogs={renderBlogs}
                params={params}
                title={title}
                onSearch={onSearch}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                pageInfo={blogsData?.pageInfo}
                search={searchText}
                showFilterChips={showFilterChips}
                selected={selected}
                toggleTopic={toggleTopic}
                noResults={
                    selectedChip
                        ? blogsData && blogsData?.blogs?.length < 1
                        : hasSearched && blogsData?.blogs?.length < 1
                }
                clearFilters={clearFilters}
                blogData={blogsData}
                country={country}
                marketingHero={marketingHero}
                marketingHeroCopyKey={marketingHeroCopyKey}
                selectedChip={selectedChip}
            />
    );
}
